import React, { useState, useEffect } from 'react';
import { X, Flag, RotateCcw, Trophy } from 'lucide-react';
import axios from 'axios';
import Connect4Board from './Connect4Board';
import CheckersBoard from './CheckersBoard';
import ChessBoard from './ChessBoard';
import BattleshipBoard from './BattleshipBoard';

const GameBoard = ({ 
  gameState, 
  gameType, 
  currentUserId,
  opponentName,
  opponentId,
  onMove, 
  onForfeit, 
  onClose 
}) => {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [showForfeitDialog, setShowForfeitDialog] = useState(false);
  
  // Fetch head-to-head stats
  useEffect(() => {
    if (!opponentId) {
      console.log('[GameBoard] No opponentId provided');
      return;
    }
    
    const fetchStats = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_BASE || '';
        const url = `${apiBase}/api/games/stats/${opponentId}`;
        console.log('[GameBoard] Fetching stats from:', url);
        
        const res = await axios.get(url, {
          withCredentials: true
        });
        
        console.log('[GameBoard] Stats response:', res.data);
        console.log('[GameBoard] Looking for gameType:', gameType);
        
        const gameStats = res.data.stats?.find(s => s.gameType === gameType);
        console.log('[GameBoard] Found stats:', gameStats);
        
        setStats(gameStats || null);
      } catch (error) {
        console.error('[GameBoard] Error fetching game stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };
    
    fetchStats();
  }, [opponentId, gameType]);
  
  if (!gameState) return null;
  
  const isMyTurn = gameState.currentPlayer === currentUserId;
  const isGameOver = gameState.winner || gameState.isDraw;
  
  const handleCloseClick = () => {
    if (!isGameOver) {
      setShowForfeitDialog(true);
    } else {
      onClose();
    }
  };
  
  const handleForfeitAndClose = () => {
    onForfeit();
    setShowForfeitDialog(false);
    // Give a moment for forfeit to process, then close
    setTimeout(() => onClose(), 500);
  };
  
  const renderBoard = () => {
    switch (gameType) {
      case 'connect4':
        return (
          <Connect4Board
            gameState={gameState}
            isMyTurn={isMyTurn}
            onMove={onMove}
            currentUserId={currentUserId}
          />
        );
      case 'checkers':
        return (
          <CheckersBoard
            gameState={gameState}
            isMyTurn={isMyTurn}
            onMove={onMove}
            currentUserId={currentUserId}
          />
        );
      case 'chess':
        return (
          <ChessBoard
            gameState={gameState}
            isMyTurn={isMyTurn}
            onMove={onMove}
            currentUserId={currentUserId}
          />
        );
      case 'battleship':
        return (
          <BattleshipBoard
            gameState={gameState}
            isMyTurn={isMyTurn}
            onMove={onMove}
            currentUserId={currentUserId}
          />
        );
      default:
        return (
          <div className="p-8 text-center text-white/70">
            Board for {gameType} coming soon!
          </div>
        );
    }
  };
  
  return (
    <div className="fixed top-0 bottom-0 left-0 right-0 md:right-[420px] z-[60] flex items-center justify-center p-2 sm:p-4 pointer-events-none">
      {/* Game Container */}
      <div className="relative w-full max-w-3xl max-h-[98vh] flex flex-col bg-slate-900/98 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden pointer-events-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-white/10 bg-slate-900/50 flex-shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-white capitalize">
              {gameType.replace(/(\d)/, ' $1')}
            </h3>
            <p className="text-sm text-white/50">vs {opponentName}</p>
            {loadingStats ? (
              <p className="text-xs text-white/30 mt-0.5">Loading record...</p>
            ) : stats ? (
              <div className="flex items-center gap-1.5 mt-1">
                <Trophy className="text-emerald-400" size={12} />
                <p className="text-xs text-emerald-400 font-medium">
                  {stats.userWins}W - {stats.opponentWins}L - {stats.draws}D
                </p>
              </div>
            ) : (
              <p className="text-xs text-white/30 mt-0.5">First game vs {opponentName}!</p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {!isGameOver && (
              <button
                onClick={onForfeit}
                className="px-3 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 text-sm font-medium transition flex items-center gap-2"
                title="Forfeit Game"
              >
                <Flag size={16} />
                <span className="hidden sm:inline">Forfeit</span>
              </button>
            )}
            <button
              onClick={handleCloseClick}
              className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition"
            >
              <X className="text-white/70" size={18} />
            </button>
          </div>
        </div>
        
        {/* Game Board */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
          {renderBoard()}
        </div>
        
        {/* Game Over Actions */}
        {isGameOver && (
          <div className="p-3 sm:p-4 border-t border-white/10 bg-slate-900/50 flex justify-center gap-3 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-medium transition"
            >
              Close
            </button>
            {/* Future: Add rematch button */}
          </div>
        )}
      </div>
      
      {/* Forfeit Confirmation Dialog */}
      {showForfeitDialog && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm pointer-events-auto">
          <div className="bg-slate-900 border border-red-500/30 rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6">
              <h3 className="text-xl font-bold text-white mb-2">Forfeit Match?</h3>
              <p className="text-white/70 text-sm">
                Closing this window will forfeit the match and count as a loss. Are you sure?
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowForfeitDialog(false)}
                className="flex-1 px-4 py-3 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 font-semibold transition"
              >
                Resume Game
              </button>
              <button
                onClick={handleForfeitAndClose}
                className="flex-1 px-4 py-3 rounded-xl bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400 font-semibold transition"
              >
                Close Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameBoard;
